/*
 * Copyright (c) Nexign, JSC, 1992-2024.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @version 1.29.0
 * @since 1.29.0
 */

import React, { ChangeEvent, FormEvent, useCallback, useMemo, useRef } from 'react';

import { FormikProps } from 'formik';
import moment, { Moment } from 'moment/moment';
import { connect, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '@/main/assets';
import { DropDownFilter, LinkBlock, RadioButton, RangeDatePicker, ReportSchedule } from '@components/common';
import { FormBlockStyled } from '@components/common/FormStyled/FormStyled';
import { EReportScheduleField, IReportScheduleData } from '@components/common/ReportSchedule/types';
import { IOptionItemProps } from '@components/common/Select/Select';
import { EBasketType } from '@modules/app/appConstants';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardAdditionalReports,
} from '@modules/internationalization/intSelectors';
import { ECalendarPeriod } from '@modules/internationalization/types/common/calendar';
import { IWizardAdditionalReportsTranslation } from '@modules/internationalization/types/wizards/additionalReports';
import { modalOpen } from '@modules/modal/modalActions';
import { wizardStorageClearStepsAction } from '@modules/wizard/wizardActions';
import { EWizardName } from '@modules/wizard/wizardConstants';
import { IWizardStorageClearStepsPayload } from '@modules/wizard/wizardTypes';
import { IAutoReportIdScheduleMeta } from '@pages/PageAutoReportId/types';
import { EFormatData } from '@pages/PageDashboard/types/widgetTypes';
import {
    WizardAdditionalReportsPeriodStyled,
    WizardAdditionalReportsStepPrepareStyled,
} from '@pages/WizardAdditionalReports/components/styled/WizardAdditionalReportsStyled';
import ChangeSubscribersModal
    from '@pages/WizardAdditionalReports/components/WizardAdditionalReportsChangeSubscribersModal';
import {
    EWizardAdditionalReports,
    EWizardAdditionalReportsSteps,
} from '@pages/WizardAdditionalReports/wizardAdditionalReportsConsant';
import {
    makeSelectWizardAdditionalReportsIsStepValid,
    makeSelectWizardAdditionalReportsParam,
} from '@pages/WizardAdditionalReports/wizardAdditionalReportsSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { openRoute } from '@routes/constants/externalRoutes';
import { RoutePath } from '@routes/constants/routesConstants';
import { htmlTranslationFormat } from '@utils/htmlTranslationFormat';
import { stringFormatParams } from '@utils/stringFormat';

import ICommonTranslation from '../../../modules/internationalization/types/common';
import { TWizardAdditionalReportsSetParamsPayload } from '../types/wizardAdditionalReportsActionTypes';
import { wizardAdditionalReportsSetParamsAction } from '../wizardAdditionalReportsActions';
import { IWizardAdditionalReportsReducer } from '../wizardAdditionalReportsReducer';

const WizardAdditionalReportsStepPrepare = ({
    autoReportDescription,
    autoReportHour,
    autoReportMinutes,
    autoReportMonth,
    autoReportType,
    autoReportWeek,
    basketType,
    chosenReport,
    formatsAvailable,
    formatChoose,
    isAutoReportEnabled,
    isCreateOrderNow,
    maxAutoReportsCount,
    newSubscribersEndDate,
    newSubscribersStartDate,
    ids,
    translation,
    translationCommon,
    setParams,
    wizardStorageClearStepsAction,
}: IConnectedProps & IDispatchProps & IAdditionalReportsPrepareProps) => {
    const dispatch = useDispatch();
    const formRef = useRef<FormikProps<IReportScheduleData>>(null);
    const calendarRef = useRef<RangeDatePicker>(null);
    const stepTranslation = translation[EWizardAdditionalReportsSteps.PrepareStep];
    const maxAutoReportsWarning: string = useMemo(() =>
            stringFormatParams(stepTranslation.maxAutoReportsWarning, { [ELocaleKeys.number]: maxAutoReportsCount }),
        [maxAutoReportsCount, stepTranslation]);
    const subscribersCount: number =  useMemo(() => ids?.filter(item => item.checked).length, [ids])

    const onSelectHour = useCallback((data: IOptionItemProps) => {
        setParams({ autoReportHour: data.value as number });
    }, [setParams]);

    const onSelectMinutes = useCallback((data: IOptionItemProps) => {
        setParams({ autoReportMinutes: data.value as number });
    }, [setParams]);

    const onSelectPeriod = useCallback((data: IOptionItemProps) => {
        setParams({ autoReportType: data.value as ECalendarPeriod });
    }, [setParams]);

    const onSelectWeek = useCallback((data: IOptionItemProps) => {
        setParams({ autoReportWeek: data.value as number });
    }, [setParams]);

    const onChangeDescription = useCallback(
        (data: React.FormEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
            setParams({ autoReportDescription: (data.target as HTMLInputElement).value });
        }, [setParams]);

    const onChangeMonth = useCallback((data: FormEvent<HTMLInputElement> | ChangeEvent<HTMLInputElement>) => {
        setParams({ autoReportMonth: (data.target as HTMLInputElement).value });
    }, [setParams]);

    const scheduleMeta: IAutoReportIdScheduleMeta = {
        [EReportScheduleField.Description]: autoReportDescription,
        [EReportScheduleField.Month]: autoReportMonth as unknown as number,
        [EReportScheduleField.Period]: { value: autoReportType },
        [EReportScheduleField.StartTime]:
            moment().startOf('day')
                .add(autoReportHour, 'hour')
                .add(autoReportMinutes, 'minute')
                .format(EFormatData.FullDataWithTimeZone),
        [EReportScheduleField.Week]: {
            value: autoReportWeek,
        },
    };

    const handleChangeSubscribersList = () => {
        dispatch(modalOpen({
                render: props => (
                    <ChangeSubscribersModal
                        {...props}
                    />
                ),
            }),
        );
    };

    const onAutoReportsLinkClick = useCallback(() => {
        wizardStorageClearStepsAction({
            wizardName: EWizardName.ADDITIONAL_REPORTS,
        });
        openRoute(RoutePath.settingsAutoReports);
    }, [wizardStorageClearStepsAction]);

    const okButtonHandler = useCallback(() => {
        if (calendarRef.current) {
            calendarRef.current.onApply();
        }
    }, [calendarRef]);

    const setRangeHandler = useCallback((leftRange: Moment, rightRange: Moment) => {
        setParams({
            newSubscribersEndDate: rightRange.format(EFormatData.FullDataWithTimeZone),
            newSubscribersStartDate: leftRange.format(EFormatData.FullDataWithTimeZone),
        });
    }, [setParams]);

    return (
        <WizardAdditionalReportsStepPrepareStyled>
            <div className='wizard-additional-reports__group-control'>
                <RadioButton
                    checked={isCreateOrderNow}
                    dataName='wizard-additional-reports-is-create-order-now'
                    label={stepTranslation.orderNow}
                    onChange={() => setParams({ isCreateOrderNow: true })}
                />
            </div>
            {
                isCreateOrderNow && !!formatsAvailable.length && (
                    <div className='wizard-additional-reports__group-control wizard-additional-reports__sub-group'>
                        <div className='wizard-additional-reports__title'>
                            {stepTranslation.reportFormat}
                        </div>
                        <div className='wizard-additional-reports__group-row'>
                            {
                                formatsAvailable.map(format => (
                                    <RadioButton
                                        checked={formatChoose === format}
                                        dataName={`wizard-additional-reports-now-format-${format}`}
                                        key={format}
                                        label={format}
                                        onChange={() => setParams({ formatChoose: format })}
                                    />
                                ))
                            }
                        </div>
                        {
                            isCreateOrderNow && chosenReport === EWizardAdditionalReports.REPORT_SUBSCRIBERS_NEW && (
                                <FormBlockStyled>
                                    <div className='wizard-additional-reports__title'>
                                        {stepTranslation.periodLabel}
                                    </div>

                                    <WizardAdditionalReportsPeriodStyled>
                                        <DropDownFilter
                                            widthAuto
                                            dataName='wizard-additional-reports-period-filter'
                                            filterButtonLabel={
                                                `${moment(newSubscribersStartDate)
                                                    .format(EFormatData.FullData)} — ${moment(newSubscribersEndDate)
                                                    .format(EFormatData.FullData)}`
                                            }
                                            icon={icons.calendarIcon}
                                            iconOpened={icons.calendarIcon}
                                            kind={'normal'}
                                            showCancelBtn={true}
                                            translation={translationCommon.dropDown}
                                            onOkClick={okButtonHandler}
                                        >
                                            <RangeDatePicker
                                                endDate={moment()}
                                                maxDate={moment()}
                                                maxYear={moment().format('YYYY')}
                                                messages={translationCommon.calendar}
                                                minYear={moment().subtract(5, 'years').format('YYYY')}
                                                ref={calendarRef}
                                                startDate={moment().startOf('month')}
                                                timePicker={false}
                                                onApply={setRangeHandler}
                                            />
                                        </DropDownFilter>
                                    </WizardAdditionalReportsPeriodStyled>
                                </FormBlockStyled>
                            )
                        }
                    </div>
                )
            }

            <div className='wizard-additional-reports__group-control'>
                <RadioButton
                    checked={!isCreateOrderNow}
                    dataName='wizard-additional-reports-is-create-order-later'
                    disabled={!isAutoReportEnabled}
                    label={stepTranslation.orderLater}
                    onChange={() => setParams({ isCreateOrderNow: false })}
                />
            </div>
            {
                !isCreateOrderNow && (
                    <div className='wizard-additional-reports__sub-group'>
                        <div className='wizard-additional-reports__info'>
                            {maxAutoReportsWarning}
                        </div>
                        {
                            (basketType === EBasketType.MOBILE) && (<div className='wizard-additional-reports__title'>
                                { stringFormatParams(
                                    stepTranslation.orderReportTitle,
                                    { [ELocaleKeys.number]: subscribersCount },
                                )}
                            </div>)
                        }
                        {
                            (basketType === EBasketType.MOBILE) && (<LinkBlock onClick={handleChangeSubscribersList}>
                                <span>{stepTranslation.changeSubscribersList}</span>
                            </LinkBlock>)
                        }
                        <div className='wizard-additional-reports__group-control'>
                            <div className='wizard-additional-reports__title'>
                                {stepTranslation.reportFormat}
                            </div>
                            <div className='wizard-additional-reports__group-row'>
                                {
                                    formatsAvailable.map(format => (
                                        <RadioButton
                                            checked={formatChoose === format}
                                            dataName={`wizard-additional-reports-format-${format}`}
                                            key={format}
                                            label={format}
                                            onChange={() => setParams({ formatChoose: format })}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                        <ReportSchedule
                            initialValue={scheduleMeta}
                            isNewSubscribersReport={chosenReport === EWizardAdditionalReports.REPORT_SUBSCRIBERS_NEW}
                            ref={formRef}
                            onChangeDescription={onChangeDescription}
                            onChangeMonth={onChangeMonth}
                            onSelectHour={onSelectHour}
                            onSelectMinutes={onSelectMinutes}
                            onSelectPeriod={onSelectPeriod}
                            onSelectWeek={onSelectWeek}
                        />
                    </div>
                )
            }
            {
                !isAutoReportEnabled && (
                    <div className='wizard-additional-reports__prompt'>
                        {htmlTranslationFormat(
                            stringFormatParams(
                                stepTranslation.tooManyAutoReports,
                                { [ELocaleKeys.number]: maxAutoReportsCount },
                            ),
                            {
                                link: <LinkBlock onClick={onAutoReportsLinkClick}>
                                    {stepTranslation.tooManyAutoReportsLink}
                                </LinkBlock>,
                        })}
                    </div>
                )
            }
        </WizardAdditionalReportsStepPrepareStyled>
    );
};

export interface IAdditionalReportsPrepareProps {
    basketType: EBasketType;
}

type TParams = Pick<IWizardAdditionalReportsReducer,
    'autoReportDescription' |
    'autoReportHour' |
    'autoReportMinutes' |
    'autoReportMonth' |
    'autoReportType' |
    'autoReportWeek' |
    'chosenReport' |
    'formatChoose' |
    'formatsAvailable' |
    'isAutoReportEnabled' |
    'isCreateOrderNow' |
    'isStepValid' |
    'maxAutoReportsCount' |
    'newSubscribersEndDate' |
    'newSubscribersStartDate' |
    'ids'
>;

export interface IConnectedProps extends TParams {
    translation: IWizardAdditionalReportsTranslation;
    translationCommon: ICommonTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    autoReportDescription: makeSelectWizardAdditionalReportsParam('autoReportDescription'),
    autoReportHour: makeSelectWizardAdditionalReportsParam('autoReportHour'),
    autoReportMinutes: makeSelectWizardAdditionalReportsParam('autoReportMinutes'),
    autoReportMonth: makeSelectWizardAdditionalReportsParam('autoReportMonth'),
    autoReportType: makeSelectWizardAdditionalReportsParam('autoReportType'),
    autoReportWeek: makeSelectWizardAdditionalReportsParam('autoReportWeek'),
    chosenReport: makeSelectWizardAdditionalReportsParam('chosenReport'),
    formatsAvailable: makeSelectWizardAdditionalReportsParam('formatsAvailable'),
    formatChoose: makeSelectWizardAdditionalReportsParam('formatChoose'),
    isAutoReportEnabled: makeSelectWizardAdditionalReportsParam('isAutoReportEnabled'),
    isCreateOrderNow: makeSelectWizardAdditionalReportsParam('isCreateOrderNow'),
    isStepValid: makeSelectWizardAdditionalReportsIsStepValid(),
    maxAutoReportsCount: makeSelectWizardAdditionalReportsParam('maxAutoReportsCount'),
    newSubscribersEndDate: makeSelectWizardAdditionalReportsParam('newSubscribersEndDate'),
    newSubscribersStartDate: makeSelectWizardAdditionalReportsParam('newSubscribersStartDate'),
    ids: makeSelectWizardAdditionalReportsParam('ids'),
    translation: makeSelectTranslationWizardAdditionalReports(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    setParams: (payload: TWizardAdditionalReportsSetParamsPayload) => void;
    wizardStorageClearStepsAction: (payload: IWizardStorageClearStepsPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setParams: payload => dispatch(wizardAdditionalReportsSetParamsAction(payload)),
    wizardStorageClearStepsAction: (payload) => dispatch(wizardStorageClearStepsAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(WizardAdditionalReportsStepPrepare);
